import { Box, CircularProgress, Container, Divider, Link, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useState, useLayoutEffect, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import SEO from "../Common/SEO";
import { getLocation } from "../locations";
import {
  veg_appetizers,
  non_veg_appetizers,
  bagaras,
  biryanis,
  speciality_rice,
  breafast_items,
  desserts,
  drinks,
} from "./menu-items";
import { MenuItem } from "./types";

const menuSections: { name: string; items: MenuItem[] }[] = [
  { name: "Veg Appetizers", items: veg_appetizers },
  { name: "Non-Veg Appetizers", items: non_veg_appetizers },
  { name: "Bagaras", items: bagaras },
  { name: "Biryanis", items: biryanis },
  { name: "Speciality Rice", items: speciality_rice },
  { name: "Breakfast Items", items: breafast_items },
  { name: "Desserts", items: desserts },
  { name: "Drinks", items: drinks },
];

const buildMenuJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Menu",
  "name": "Bagara's Menu",
  "description":
    "Full menu for Bagara's Indian restaurant in Sunnyvale — Hyderabadi biryani, bagara rice, Telugu curries, appetizers, desserts & drinks.",
  "url": "https://bagaras.com/menu",
  "hasMenuSection": menuSections.map((section) => ({
    "@type": "MenuSection",
    "name": section.name,
    "hasMenuItem": section.items
      .filter((item) => item.isAvailable)
      .map((item) => ({
        "@type": "MenuItem",
        "name": item.name,
        "offers": {
          "@type": "Offer",
          "price": item.price.toFixed(2),
          "priceCurrency": "USD",
        },
      })),
  })),
});

const MenuOrder = () => {
  const [searchParams] = useSearchParams();
  const branch = getLocation(searchParams.get("location"));
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const menuTitle = branch
    ? `Order Bagara's ${branch.name} - ${branch.service}`
    : "Order Bagara's - Choose a location";

  // Set title synchronously so react-snap captures it before Helmet's RAF fires
  useLayoutEffect(() => {
    document.title = menuTitle;
  }, [menuTitle]);

  useEffect(() => {
    setIframeLoaded(false);
  }, [branch?.id]);

  if (!branch) {
    return <Navigate to="/order-now" replace />;
  }

  return (
    <div className="menu-order-frame" style={{
      margin: 0,
      padding: 0,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <SEO
        title={menuTitle}
        description={`Order from Bagara's ${branch.name}. ${branch.service}. ${branch.address}. ${branch.hoursSummary}`}
        canonical={`https://bagaras.com/menu?location=${branch.id}`}
        keywords="Bagara's menu, order biryani online, Hyderabadi biryani, Bagara's Sunnyvale, Bagara's San Francisco, Indian food delivery, Indian takeout"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(buildMenuJsonLd())}
        </script>
      </Helmet>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1,
          backgroundColor: "#20141e",
          color: "white",
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" sx={{ color: "white" }}>
          <strong>{branch.name}</strong>
          {" · "}
          {branch.service}
        </Typography>
        <Link href="/order-now" sx={{ color: "#FFD700", fontWeight: 700, whiteSpace: "nowrap" }}>
          Change location
        </Link>
      </Box>

      <Box sx={{ width: "100%", m: 0, p: 0, position: "relative", flex: 1, minHeight: 0 }}>
        {!iframeLoaded && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              zIndex: 1,
            }}
          >
            <CircularProgress size={48} sx={{ color: "#20141e" }} />
          </Box>
        )}
        <iframe
          key={branch.id}
          src={branch.orderUrl}
          title={`Order from Bagara's ${branch.name}`}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            border: "none",
            margin: 0,
            padding: 0,
          }}
          loading="lazy"
          allow="payment"
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => setIframeLoaded(true)}
        />
      </Box>

      {/* Crawlable menu content for SEO — visually hidden but indexable by search engines */}
      <Container
        sx={{
          py: 4,
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          borderWidth: 0,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
          Bagara&#39;s Menu &amp; Online Ordering
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Authentic Hyderabadi biryani, bagara rice, Telugu curries, appetizers, desserts &amp; drinks.
          Order online for delivery or takeout in Sunnyvale and the Bay Area.
        </Typography>

        {menuSections.map((section) => (
          <Box key={section.name} sx={{ mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mb: 1 }}>
              {section.name}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {section.items
              .filter((item) => item.isAvailable)
              .map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.5,
                  }}
                >
                  <Typography variant="body1">{item.name}</Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    ${item.price.toFixed(2)}
                  </Typography>
                </Box>
              ))}
          </Box>
        ))}
      </Container>
    </div>
  );
};

export default MenuOrder;
